import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MessageSquare, 
  Cpu, 
  ClipboardList, 
  ArrowRight,
  CheckCircle,
  Zap,
  Clock,
  TrendingDown,
  Shield,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui';
import SEO, { getBreadcrumbSchema } from '../components/SEO';

const HowItWorksPage = () => {
  const steps = [
    {
      number: 1,
      icon: MessageSquare,
      title: 'Describe Your Project',
      description: 'Tell us what you\'re building in plain English. Include dimensions, materials, or just describe your project.',
      examples: [
        '"I need to pour a 10x12 concrete patio, 4 inches thick"',
        '"Drywall for a 12x14 room with 9 foot ceilings"',
        '"Paint 3 bedrooms, each about 12x12 feet"',
      ],
      tips: [
        'Include specific dimensions when possible',
        'Mention the type of material you prefer',
        'Add details like number of coats or special requirements',
      ],
    },
    {
      number: 2,
      icon: Cpu,
      title: 'AI Calculates Instantly',
      description: 'Our AI understands construction terminology and calculates exact quantities using industry-standard formulas.',
      features: [
        'Trained on real construction data',
        'Uses industry-standard waste factors',
        'Accounts for material-specific requirements',
        'Provides cost estimates based on market prices',
      ],
      highlight: 'Results in under 2 seconds',
    },
    {
      number: 3,
      icon: ClipboardList,
      title: 'Get Your Material List',
      description: 'Receive a detailed breakdown with quantities, costs, and a ready-to-use shopping list.',
      includes: [
        '📦 Exact quantities with units',
        '💰 Estimated cost range',
        '📋 Shopping list format',
        '💡 Pro tips for your project',
      ],
    },
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Save Hours',
      description: 'Get estimates in seconds instead of spending hours with spreadsheets and calculators.',
    },
    {
      icon: TrendingDown,
      title: 'Reduce Waste',
      description: 'Accurate calculations with proper waste factors mean you buy exactly what you need.',
    },
    {
      icon: Shield,
      title: 'Professional Accuracy',
      description: 'Calculations based on industry standards used by professional contractors.',
    },
    {
      icon: Zap,
      title: 'Always Available',
      description: 'Calculate materials anytime, anywhere. Works on desktop and mobile.',
    },
  ];

  const materialTypes = [
    { name: 'Concrete', description: 'Patios, slabs, foundations, walkways', path: '/concrete-calculator' },
    { name: 'Drywall', description: 'Walls, ceilings, rooms, basements', path: '/drywall-calculator' },
    { name: 'Paint', description: 'Interior, exterior, rooms, trim', path: '/paint-calculator' },
    { name: 'Flooring', description: 'Hardwood, laminate, tile, vinyl', path: '/flooring-calculator' },
    { name: 'Lumber', description: 'Decks, fences, framing, sheds', path: '/lumber-calculator' },
    { name: 'Roofing', description: 'Shingles, underlayment, flashing', path: '/roofing-calculator' },
  ];

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'How It Works', path: '/how-it-works' },
  ];

  return (
    <>
      <SEO
        title="How It Works"
        description="Learn how CalcBuildAI's AI-powered construction calculator works. Describe your project, get instant calculations, and receive a detailed material list."
        canonical="/how-it-works"
        structuredData={getBreadcrumbSchema(breadcrumbs)}
      />

      <div className="min-h-screen">
        {/* Hero */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            {/* Breadcrumb */}
            <nav className="flex justify-center mb-6 text-sm">
              <ol className="flex items-center gap-2">
                <li><Link to="/" className="text-gray-500 hover:text-blue-600">Home</Link></li>
                <li className="text-gray-400">/</li>
                <li className="text-gray-900 font-medium">How It Works</li>
              </ol>
            </nav>

            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              AI-Powered Calculator
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How CalcBuildAI Works
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get accurate construction material estimates in seconds. 
              Just describe your project and let AI do the math.
            </p>
          </div>
        </section>

        {/* Steps */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <div className="space-y-16">
              {steps.map((step, idx) => (
                <div 
                  key={step.number}
                  className={`flex flex-col ${idx % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 items-center`}
                >
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {step.number}
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                        {step.title}
                      </h2>
                    </div>
                    
                    <p className="text-lg text-gray-600 mb-6">
                      {step.description}
                    </p>

                    {step.examples && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Example inputs:</h4>
                        <div className="space-y-2">
                          {step.examples.map((example, i) => (
                            <div key={i} className="bg-gray-50 rounded-lg px-4 py-2 text-gray-700 italic">
                              {example}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {step.tips && (
                      <div className="bg-blue-50 rounded-xl p-4">
                        <h4 className="font-semibold text-blue-900 mb-2">💡 Tips for best results:</h4>
                        <ul className="space-y-1">
                          {step.tips.map((tip, i) => (
                            <li key={i} className="flex items-start gap-2 text-blue-800">
                              <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {step.features && (
                      <div className="space-y-2">
                        {step.features.map((feature, i) => (
                          <div key={i} className="flex items-center gap-2 text-gray-700">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span>{feature}</span>
                          </div>
                        ))}
                        {step.highlight && (
                          <div className="mt-4 inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full font-medium">
                            <Zap className="w-4 h-4" />
                            {step.highlight}
                          </div>
                        )}
                      </div>
                    )}

                    {step.includes && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Your results include:</h4>
                        <div className="space-y-2">
                          {step.includes.map((item, i) => (
                            <div key={i} className="text-gray-700">{item}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-48 h-48 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl flex items-center justify-center">
                      <step.icon className="w-24 h-24 text-blue-600" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Why Use CalcBuildAI?
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Material Types */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
              What Can You Calculate?
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              CalcBuildAI supports all major construction materials with specialized calculators
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {materialTypes.map((material, idx) => (
                <Link
                  key={idx}
                  to={material.path}
                  className="flex items-center justify-between p-4 bg-gray-50 hover:bg-blue-50 rounded-xl border border-gray-200 hover:border-blue-200 transition-colors group"
                >
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-blue-700">
                      {material.name}
                    </h3>
                    <p className="text-sm text-gray-500">{material.description}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Try It?
            </h2>
            <p className="text-blue-100 mb-8 text-lg">
              Start calculating materials for your next project in seconds
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-600 hover:bg-blue-50"
                  icon={ArrowRight}
                  iconPosition="right"
                >
                  Try Free Calculator
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  size="lg" 
                  className="border-2 border-white text-white hover:bg-white/20 bg-transparent"
                >
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HowItWorksPage;
